import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Home, GraduationCap, MapPin, Users, Camera } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from '../ui/ThemeToggle';

const navigation = [
  { name: 'Beranda', href: '/', icon: Home },
  { name: 'Program', href: '/program', icon: GraduationCap },
  { name: 'Desa', href: '/desa', icon: MapPin },
  { name: 'Tim KKN', href: '/kkn', icon: Users },
  { name: 'Dokumentasi', href: '/dokumentasi', icon: Camera },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const location = useLocation();

  // Detect dark mode using class on html element (tailwind dark mode)
  useEffect(() => {
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        scrolled 
          ? 'backdrop-blur-xl bg-white/20 dark:bg-slate-900/20 shadow-2xl shadow-blue-500/5 dark:shadow-slate-900/20 border-b border-white/30 dark:border-slate-700/30' 
          : 'backdrop-blur-sm bg-transparent'
      }`}
    >
      {/* Glassmorphism overlay */}
      <div className={`absolute inset-0 transition-all duration-500 ${
        scrolled 
          ? 'bg-gradient-to-r from-blue-50/30 via-white/30 to-purple-50/30 dark:from-slate-900/30 dark:via-slate-800/30 dark:to-slate-900/30 backdrop-blur-xl' 
          : 'bg-transparent'
      }`} />
      
      <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
        <div className="flex justify-between items-center h-16 sm:h-18 lg:h-20">
          
          {/* Logo Section */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center space-x-2 sm:space-x-3 z-10"
          >
            {/* Logo Icon */}
            <motion.div 
              className="flex items-center justify-center transition-all duration-300"
              whileHover={{ rotate: 5, scale: 1.1 }}
            >
              <img 
                src={isDark ? "/assets/logo/logo_kkn_putih.png" : "/assets/logo/logo_kkn_hitam.png"}
                alt="Logo KKN" 
                className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 object-contain drop-shadow-lg transition-all duration-300"
              />
            </motion.div>
            
            {/* Logo Text */}
            <div className="block">
              <motion.h1 
                className={`text-sm sm:text-base lg:text-lg font-bold transition-all duration-300 ${
                  scrolled 
                    ? 'text-slate-800 dark:text-white' 
                    : 'text-slate-800 dark:text-white drop-shadow-lg'
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="block sm:hidden">KKN DESA KARANGMALANG</span>
                <span className="hidden sm:block">KKN DESA KARANGMALANG</span>
              </motion.h1>
              <motion.p 
                className={`text-xs sm:text-sm transition-all duration-300 ${
                  scrolled 
                    ? 'text-slate-600 dark:text-slate-300' 
                    : 'text-slate-600 dark:text-slate-300 drop-shadow-md'
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <span className="block sm:hidden">Universitas Jenderal Soedirman</span>
                <span className="hidden sm:block lg:block">UNIVERSITAS JENDERAL SOEDIRMAN</span>
              </motion.p>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1 xl:space-x-2">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.4 }}
                  whileHover={{ y: -2, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to={item.href}
                    className={`group relative flex items-center space-x-2 px-3 xl:px-4 py-2.5 rounded-xl xl:rounded-2xl text-sm font-medium transition-all duration-300 ${
                      isActive
                        ? 'text-blue-600 dark:text-blue-400 bg-white/50 dark:bg-slate-800/50 shadow-lg'
                        : scrolled 
                          ? 'text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white/40 dark:hover:bg-slate-800/40 hover:shadow-lg hover:shadow-blue-500/10 backdrop-blur-sm' 
                          : 'text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white/30 dark:hover:bg-slate-800/30 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-500/20'
                    }`}
                  >
                    {/* Background blur effect */}
                    <div className="absolute inset-0 rounded-xl xl:rounded-2xl bg-gradient-to-r from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/10 group-hover:to-purple-500/10 transition-all duration-300 backdrop-blur-sm" />
                    
                    <Icon className="h-4 w-4 z-10 transition-transform duration-200 group-hover:scale-110" />
                    <span className="z-10 hidden xl:block">{item.name}</span>
                    
                    {/* Active indicator */}
                    <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300 ${
                      isActive ? 'w-6 xl:w-8' : 'w-0 group-hover:w-6 xl:group-hover:w-8'
                    }`} />
                  </Link>
                </motion.div>
              );
            })}
            
            {/* Theme Toggle */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="ml-2 xl:ml-4"
            >
              <ThemeToggle className="scale-90 xl:scale-100" />
            </motion.div>
          </div>

          {/* Mobile menu button and theme toggle */}
          <div className="lg:hidden flex items-center space-x-2 sm:space-x-3">
            <ThemeToggle className="scale-75 sm:scale-90" />
            <motion.button
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => setIsOpen(!isOpen)}
              className={`relative p-2 sm:p-2.5 rounded-xl sm:rounded-2xl transition-all duration-300 ${
                scrolled 
                  ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white/40 dark:hover:bg-slate-800/40 hover:shadow-lg backdrop-blur-sm' 
                  : 'text-slate-700 dark:text-slate-200 hover:bg-white/30 dark:hover:bg-slate-800/30 backdrop-blur-sm hover:shadow-lg'
              }`}
            >
              <div className="relative z-10">
                <AnimatePresence mode="wait">
                  {isOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <X className="h-5 w-5 sm:h-6 sm:w-6" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden relative"
          >
            {/* Glassmorphism background */}
            <div className="absolute inset-0 bg-white/20 dark:bg-slate-900/20 backdrop-blur-xl border-t border-white/30 dark:border-slate-700/30" />
            
            <div className="relative px-3 sm:px-4 py-4 sm:py-6 space-y-1 sm:space-y-2">
              {navigation.map((item, index) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                    whileHover={{ x: 4, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Link
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`group flex items-center space-x-3 sm:space-x-4 px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl text-sm font-medium transition-all duration-300 ${
                        isActive
                          ? 'text-blue-600 dark:text-blue-400 bg-white/50 dark:bg-slate-800/50 shadow-lg'
                          : 'text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white/40 dark:hover:bg-slate-800/40 hover:shadow-lg hover:shadow-blue-500/10 backdrop-blur-sm'
                      }`}
                    >
                      <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 backdrop-blur-sm ${
                        isActive
                          ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20'
                          : 'bg-gradient-to-br from-blue-500/10 to-purple-500/10 group-hover:from-blue-500/20 group-hover:to-purple-500/20'
                      }`}>
                        <Icon className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 group-hover:scale-110" />
                      </div>
                      <span className="font-medium text-sm sm:text-base">{item.name}</span>
                      
                      {/* Arrow indicator */}
                      <motion.div 
                        className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        whileHover={{ x: 2 }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </motion.div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}